import { NgIf } from '@angular/common';
import { Component, effect, OnDestroy, OnInit, signal, Signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { APP_VERSION } from 'app/app-version';
import { AppLogoDirective } from 'app/directives/app-logo.directive';
import { AuthService, ROL_ADMIN, ROL_SUPERVISOR } from 'app/core/auth/auth.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Sucursal, SucursalService } from 'app/services/api/sucursal.service';
import { AlertService } from 'app/services/local/alert.service';
import { map, Observable, Subject, takeUntil } from 'rxjs';

@Component({
    selector     : 'classy-layout',
    templateUrl  : './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [
      FuseLoadingBarComponent,
      FuseVerticalNavigationComponent,
      UserComponent,
      NgIf,
      MatIconModule,
      MatButtonModule,
      FuseFullscreenComponent,
      RouterOutlet,
      MatFormFieldModule,
      MatSelectModule,
      AppLogoDirective
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy
{
    readonly version = APP_VERSION;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    sucursales = signal<Sucursal[]>([]);
    sucursalSeleccionada = signal<number | null>(null);
    showSucursales$: Observable<boolean>;
    sinSucursales = signal(false);

    /**
     * Constructor
     */
    constructor(
        private alertService: AlertService,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private sucursalService: SucursalService,
        authService: AuthService
    )
    {
      effect(() => {
        const _ = sucursalService.cambio();
        sucursalService.getSucursalesDeUsuario().then(this.actualizarSucursales.bind(this));
      });
      this.showSucursales$ = authService.getUser().pipe(
        map(user => user.roles.some(r => r.clave === ROL_ADMIN || r.clave === ROL_SUPERVISOR))
      );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) =>
            {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

      this.sucursalService.getSucursalesDeUsuario().then(this.actualizarSucursales.bind(this));
    }

    actualizarSucursales(sucursales: Sucursal[]) {
      this.sucursales.set(sucursales);
      if (sucursales.length > 0) {
        const sucursalActual = this.sucursalService.sucursalActual();
        const sucursal = sucursales.find(s => s.id === sucursalActual);
        const sucursalId = sucursal ? sucursal.id : sucursales[0].id;

        if (sucursalId && sucursalId !== this.sucursalSeleccionada()) {
          this.cambiarSucursal(sucursalId);
        }

        this.sucursalSeleccionada.set(sucursalId);
        this.sucursalService.sucursalActual.set(sucursalId);
        this.sinSucursales.set(false);
      } else {
        this.alertService.setAlert('El usuario no tiene sucursales activas asignadas.');
        this.sinSucursales.set(true);
        this.navigation = { compact: [], default: [], futuristic: [], horizontal: [] };
      }
    }

    cambiarSucursal(sucursalId: number) {
      this.sucursalSeleccionada.set(sucursalId);
      this.sucursalService.sucursalActual.set(sucursalId);
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router.navigate([this._router.url]);
      });
    }


    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
